import { useContext, useEffect, useRef } from "react";
import { useQuery } from "@apollo/client";
import { useLocation, useMatches } from "react-router-dom";
import { AnalyticsContext } from "@/contexts/AnalyticsProvider";
import { CURRENT_USER } from "@/api/queries/currentUser";
import { useOrg } from "@/hooks/useOrg";
import { useOrgPermissions } from "@/hooks/useOrgPermissions";
import { PartnerFeatureEnum } from "@/__generated__/types";
import { RouteHandle } from "@/routes";

// Analytics event names - enum values map to the exact event strings sent to Segment
export enum AnalyticsEvent {
  PageViewed = "Page Viewed",
  OpenedStudentList = "Opened Student List",
  OpenedProfile = "Opened Profile",
  StudentFilterApplied = "Student Filter Applied",
  FunnelFilterApplied = "Funnel Filter Applied",
  FunnelStateClicked = "Funnel State Clicked",
}

export function useAnalytics() {
  const context = useContext(AnalyticsContext);

  if (!context) {
    throw new Error("useAnalytics must be used within AnalyticsProvider");
  }

  return {
    trackEvent: context.track,
    resetIdentity: context.reset,
  };
}

/**
 * Hook that identifies the current user with Segment.
 */
export function useSegmentIdentify() {
  const context = useContext(AnalyticsContext);
  const { data: userData, loading: userLoading } = useQuery(CURRENT_USER);
  const { org, loading: orgLoading } = useOrg();
  const {
    getOrgPartnershipLevel,
    hasOrgFeature,
    loading: permissionsLoading,
  } = useOrgPermissions();

  useEffect(() => {
    if (!context) {
      return;
    }

    if (userLoading || orgLoading || permissionsLoading) {
      return;
    }

    if (!userData?.me || !org) {
      return;
    }

    const user = userData.me;
    const partnershipLevel = getOrgPartnershipLevel();
    const hasRise = hasOrgFeature(PartnerFeatureEnum.Rise);

    context.identify(user.uuid, {
      "User UUID": user.uuid,
      "Email": user.email,
      "Name": user.name,
      "Role": user.role,
      "Org UUID": org.uuid,
      "Org Name": org.name,
      "Partner Level": partnershipLevel,
      "Partner Tier": hasRise ? "RISE" : "Insights",
    });
  }, [
    context,
    userData,
    org,
    userLoading,
    orgLoading,
    permissionsLoading,
    getOrgPartnershipLevel,
    hasOrgFeature,
  ]);
}

/**
 * Hook that tracks page views when the route changes.
 * Uses the analyticsName from the deepest matching route handle.
 */
export function usePageViewTracking() {
  const { trackEvent } = useAnalytics();
  const location = useLocation();
  const matches = useMatches();
  const lastTrackedPath = useRef<string | null>(null);

  useEffect(() => {
    // Avoid tracking the same path twice (e.g., on re-renders)
    if (lastTrackedPath.current === location.pathname) {
      return;
    }

    // Get the deepest route that has an analyticsName
    const currentMatch = matches
      .filter((m) => (m.handle as RouteHandle | undefined)?.analyticsName)
      .pop();

    const analyticsName = (currentMatch?.handle as RouteHandle | undefined)
      ?.analyticsName;

    if (analyticsName) {
      trackEvent(AnalyticsEvent.PageViewed, {
        Page: analyticsName,
      });
      lastTrackedPath.current = location.pathname;
    }
  }, [trackEvent, location.pathname, matches]);
}
